import JSZip from 'jszip'
import { STICKER_SIZE, type Face, type Raster } from './geometry'

export type PackItem = {
  id: string
  sticker: Raster
  face: Face
}

const encodeWebp = (image: Raster) =>
  new Promise<Blob>((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = STICKER_SIZE
    canvas.height = STICKER_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      reject(new Error('无法编码 WebP'))
      return
    }
    ctx.drawImage(image, 0, 0)
    canvas.toBlob(
      (blob) => {
        if (blob && blob.type === 'image/webp') resolve(blob)
        else reject(new Error('浏览器不能导出 WebP，请用 Chrome'))
      },
      'image/webp',
      0.9,
    )
  })

async function fillZip(folder: JSZip, items: PackItem[], onItem?: () => void) {
  const stickers = items.map((item) => ({ id: item.id, face: item.face }))
  folder.file(
    'config.json',
    `${JSON.stringify({ canvas: [STICKER_SIZE, STICKER_SIZE], stickers }, null, 2)}\n`,
  )
  for (const item of items) {
    folder.file(`${item.id}.webp`, await encodeWebp(item.sticker))
    onItem?.()
  }
}

/** 一个语言的客户端包：根目录 config.json + {id}.webp。 */
export async function buildLangZip(items: PackItem[], onItem?: () => void) {
  const zip = new JSZip()
  await fillZip(zip, items, onItem)
  return zip.generateAsync({ type: 'blob', compression: 'STORE' })
}

/** 画师产出：根上是定位脸，每个语言目录里是 {id}.png 和 {id}_face.png。 */
export async function buildArtistZip(
  tid: string,
  kitPng: Blob | null,
  groups: { lang: string; items: { id: string; sticker: Blob; locator: Blob }[] }[],
) {
  const zip = new JSZip()
  if (kitPng) zip.file(`${tid}_face_kit.png`, kitPng)
  for (const group of groups) {
    const folder = zip.folder(group.lang)
    if (!folder) throw new Error('无法创建画师包')
    for (const item of group.items) {
      folder.file(`${item.id}.png`, item.sticker)
      folder.file(`${item.id}_face.png`, item.locator)
    }
  }
  return zip.generateAsync({ type: 'blob', compression: 'STORE' })
}

/** 全部语言打在一个 zip 里，每个语言一个目录。 */
export async function buildAllZip(groups: { lang: string; items: PackItem[] }[]) {
  const zip = new JSZip()
  for (const group of groups) {
    await fillZip(zip.folder(group.lang) ?? zip, group.items)
  }
  return zip.generateAsync({ type: 'blob', compression: 'STORE' })
}

const STARTER_SCRIPTS = ['1_新建贴纸文档.jsx', '2_导出素材包.jsx']

/** 发给画师的起始包：标准脸、三个空语言目录、两个 Photoshop 脚本。 */
export async function buildStarterZip(tid: string, faceKitPng: Blob) {
  const zip = new JSZip()
  const root = zip.folder(tid)
  if (!root) throw new Error('无法创建起始包')
  root.file(`${tid}_face_kit.png`, faceKitPng)
  for (const lang of ['en', 'pt', 'es']) root.folder(lang)
  const base = process.env.PUBLIC_URL || ''
  for (const name of STARTER_SCRIPTS) {
    const res = await fetch(`${base}/sticker/${encodeURIComponent(name)}`)
    if (!res.ok) throw new Error(`缺少画师脚本 ${name}`)
    zip.file(name, await res.arrayBuffer())
  }
  return zip.generateAsync({ type: 'blob', compression: 'STORE' })
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
